import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import { X, Download, Eye } from 'lucide-react'
import { format } from 'date-fns'

const statusColors = {
  paid: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
}

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value || 0)

const formatDate = (value) => {
  if (!value) return 'N/A'
  try {
    return format(new Date(value), 'MMM dd, yyyy')
  } catch {
    return 'Invalid date'
  }
}

export default function InvoiceSummaryModal({ isOpen, onClose, organization, invoices, periodLabel, onViewInvoice, onDownloadInvoice }) {
  if (!isOpen) return null

  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0)
  const paidCount = invoices.filter((inv) => inv.status === 'paid').length
  const pendingCount = invoices.filter((inv) => inv.status === 'pending').length
  const failedCount = invoices.filter((inv) => inv.status === 'failed').length

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-xl font-semibold">{periodLabel} Summary</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <div className="space-y-6">
            {/* Organization Info */}
            <div className="border-b border-gray-200 pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {organization?.name || 'N/A'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {organization?.email || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-xs uppercase text-gray-500 mb-1">Total Amount</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(totalAmount)}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500 mb-1">Paid</p>
                <p className="text-lg font-semibold text-gray-900">{paidCount}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500 mb-1">Pending</p>
                <p className="text-lg font-semibold text-gray-900">{pendingCount}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500 mb-1">Failed</p>
                <p className="text-lg font-semibold text-gray-900">{failedCount}</p>
              </div>
            </div>

            {/* Invoice Table */}
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <p className="text-sm font-semibold text-gray-900">Invoice Breakdown</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{invoice.productName}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(invoice.issueDate)}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                          {formatCurrency(invoice.amount)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-xs font-medium px-2 py-1 rounded-full ${
                              invoice.status === 'paid' 
                                ? statusColors.paid
                                : invoice.status === 'pending'
                                ? statusColors.pending
                                : statusColors.failed
                            }`}
                          >
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (onViewInvoice) {
                                  onViewInvoice(invoice.id, organization?.id)
                                }
                              }}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="View Invoice"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (onDownloadInvoice) {
                                  onDownloadInvoice(organization, invoice)
                                }
                              }}
                              className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                              title="Download Invoice"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

